import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Star,
  Trash2,
} from 'lucide-react'
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react'

import {
  ACCEPTED_UPLOAD_MIME_TYPES,
  MAX_IMAGES_PER_PRODUCT,
  MAX_UPLOAD_BYTES,
} from '@aeris/catalog'
import type { ProductImageSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Spinner,
  Tooltip,
} from '@aeris/ui'

import {
  ApiError,
  deleteProductImage,
  fetchProductImages,
  reorderProductImages,
  updateProductImage,
  uploadProductImage,
} from '../lib/api.ts'

/**
 * The photographs of one product.
 *
 * "Main photograph plus gallery" is one list with a flag rather than two
 * fields: a separate `mainImage` and `otherImages` cannot express "promote
 * this one" without moving a row between two collections, and every screen
 * would have to remember which collection it was reading.
 *
 * Every mutation answers with the whole list, so this component never
 * reconstructs order or primacy locally — it renders what the server said.
 */

const megabytes = (bytes: number): string =>
  `${(bytes / (1024 * 1024)).toFixed(1)} MB`

export type ProductGalleryProps = {
  productId: string
  canWrite: boolean
}

export const ProductGallery = ({
  productId,
  canWrite,
}: ProductGalleryProps): ReactNode => {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  /*
   * Apagar uma foto era um clique num ícone de lixeira do tamanho de um
   * "Editar", e não tem volta: o arquivo sai do armazenamento e a única saída é
   * subir de novo, se ainda existir no celular de quem fotografou (F069).
   */
  const [removing, setRemoving] = useState<ProductImageSummary | null>(null)

  const query = useQuery({
    queryKey: ['product-images', productId],
    queryFn: ({ signal }) => fetchProductImages(productId, signal),
  })

  const images = query.data?.images ?? []

  /**
   * Invalidates the product list as well as this gallery.
   *
   * The list row shows the main photograph, so promoting or deleting one here
   * changes a screen this component cannot see.
   */
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['product-images', productId],
    })
    await queryClient.invalidateQueries({ queryKey: ['products'] })
  }

  const describe = (cause: unknown): string =>
    cause instanceof ApiError
      ? cause.message
      : 'Não foi possível concluir. Tente novamente.'

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      // One request per file, in sequence rather than at once: the server takes
      // one file per request, and a browser firing twelve uploads in parallel
      // on a shop's connection makes all twelve slow.
      for (const file of files) {
        await uploadProductImage(productId, file)
      }
    },
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (cause) => {
      setError(describe(cause))
    },
  })

  const mutate = useMutation({
    mutationFn: async (action: () => Promise<unknown>) => await action(),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (cause) => {
      setError(describe(cause))
    },
  })

  const accept = (incoming: FileList | null): void => {
    if (incoming === null || incoming.length === 0) return

    const files = [...incoming]
    const room = MAX_IMAGES_PER_PRODUCT - images.length

    if (room <= 0) {
      setError(
        `Este produto já tem ${String(MAX_IMAGES_PER_PRODUCT)} imagens, o máximo.`
      )
      return
    }

    const tooBig = files.find((file) => file.size > MAX_UPLOAD_BYTES)
    if (tooBig !== undefined) {
      // Caught here so a 12 MB photograph is not uploaded in full before being
      // refused. The server checks again regardless.
      setError(
        `“${tooBig.name}” tem ${megabytes(tooBig.size)}. O limite é ${megabytes(MAX_UPLOAD_BYTES)}.`
      )
      return
    }

    setError(null)
    upload.mutate(files.slice(0, room))
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>): void => {
    accept(event.target.files)
    // Cleared so choosing the same file twice in a row still fires a change.
    event.target.value = ''
  }

  const move = (image: ProductImageSummary, delta: number): void => {
    const order = images.map((entry) => entry.id)
    const from = order.indexOf(image.id)
    const to = from + delta
    if (from < 0 || to < 0 || to >= order.length) return

    const reordered = [...order]
    const [moved] = reordered.splice(from, 1)
    if (moved === undefined) return
    reordered.splice(to, 0, moved)

    mutate.mutate(async () => await reorderProductImages(productId, reordered))
  }

  const busy = upload.isPending || mutate.isPending

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          Fotos
        </h3>
        <span className="text-(length:--text-xs) text-(--text-muted)">
          {images.length} de {MAX_IMAGES_PER_PRODUCT}
        </span>
      </div>

      {query.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-2">
          {images.map((image, index) => (
            <figure
              key={image.id}
              className="group relative flex flex-col gap-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-1"
            >
              <div className="relative aspect-square overflow-hidden rounded-(--radius-sm) bg-(--surface-sunken)">
                <img
                  src={image.thumbUrl}
                  alt={image.altText ?? ''}
                  width={image.width}
                  height={image.height}
                  loading="lazy"
                  className="h-full w-full object-contain"
                />
                {image.isPrimary ? (
                  <Badge tone="info" className="absolute left-1 top-1">
                    Principal
                  </Badge>
                ) : null}
              </div>

              {canWrite ? (
                <div className="flex items-center justify-between gap-0.5">
                  <Tooltip
                    content={
                      image.isPrimary
                        ? 'Já é a foto principal'
                        : 'Usar como foto principal'
                    }
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Usar “${image.altText ?? `foto ${String(index + 1)}`}” como principal`}
                      aria-pressed={image.isPrimary}
                      disabled={image.isPrimary || busy}
                      onClick={() => {
                        mutate.mutate(
                          async () =>
                            await updateProductImage(productId, image.id, {
                              isPrimary: true,
                            })
                        )
                      }}
                    >
                      <Star
                        aria-hidden="true"
                        className="h-3.5 w-3.5"
                        fill={image.isPrimary ? 'currentColor' : 'none'}
                      />
                    </Button>
                  </Tooltip>

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Mover para a esquerda"
                    disabled={index === 0 || busy}
                    onClick={() => {
                      move(image, -1)
                    }}
                  >
                    <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Mover para a direita"
                    disabled={index === images.length - 1 || busy}
                    onClick={() => {
                      move(image, 1)
                    }}
                  >
                    <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remover foto"
                    disabled={busy}
                    onClick={() => {
                      setRemoving(image)
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : null}

              {canWrite ? (
                /**
                 * Alternative text, saved when the field loses focus.
                 *
                 * Present rather than optional-and-forgotten: a catalogue read
                 * by a screen reader is a list of "imagem" without it, and the
                 * person best placed to write it is the one looking at the
                 * photograph right now.
                 */
                <Input
                  defaultValue={image.altText ?? ''}
                  placeholder="Descrição"
                  aria-label={`Descrição da foto ${String(index + 1)}`}
                  className="h-6 text-(length:--text-xs)"
                  onKeyDown={(event) => {
                    // This field lives inside the product form. Without this,
                    // Enter would submit the product and close the dialog while
                    // somebody was only naming a photograph.
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      event.currentTarget.blur()
                    }
                  }}
                  onBlur={(event) => {
                    const next = event.target.value.trim()
                    if (next === (image.altText ?? '')) return
                    mutate.mutate(
                      async () =>
                        await updateProductImage(productId, image.id, {
                          altText: next,
                        })
                    )
                  }}
                />
              ) : null}
            </figure>
          ))}

          {canWrite && images.length < MAX_IMAGES_PER_PRODUCT ? (
            <div
              // A drop target and a button. The click path exists because drag
              // and drop is unavailable from a keyboard and awkward on a touch
              // screen at a counter.
              onDragOver={(event) => {
                event.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => {
                setDragging(false)
              }}
              onDrop={(event) => {
                event.preventDefault()
                setDragging(false)
                accept(event.dataTransfer.files)
              }}
              className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-(--radius-md) border border-dashed p-2 text-center transition-colors ${
                dragging
                  ? 'border-(--brand-primary) bg-(--surface-hover)'
                  : 'border-(--border-default)'
              }`}
            >
              {upload.isPending ? (
                <Spinner />
              ) : (
                <>
                  <ImagePlus
                    aria-hidden="true"
                    className="h-5 w-5 text-(--text-muted)"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      fileInput.current?.click()
                    }}
                  >
                    Adicionar
                  </Button>
                  <span className="text-(length:--text-xs) text-(--text-muted)">
                    ou arraste aqui
                  </span>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}

      {!query.isPending && images.length === 0 && !canWrite ? (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Este produto não tem fotos.
        </p>
      ) : null}

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED_UPLOAD_MIME_TYPES}
        multiple
        onChange={onPick}
        className="hidden"
        // Labelled for the accessibility tree even though it is driven by the
        // button above it.
        aria-label="Escolher fotos"
      />

      {error !== null ? (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      ) : null}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setRemoving(null)
        }}
        title="Remover esta foto?"
        description={
          removing === null
            ? ''
            : `A foto ${removing.altText ?? 'sem descrição'} sai do produto e do armazenamento. Não dá para desfazer — para tê-la de volta é preciso subir o arquivo outra vez.`
        }
        confirmLabel="Remover a foto"
        loading={mutate.isPending}
        onConfirm={() => {
          const alvo = removing
          setRemoving(null)
          if (alvo !== null) {
            mutate.mutate(
              async () => await deleteProductImage(productId, alvo.id)
            )
          }
        }}
      />
    </section>
  )
}
